import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { to: "/", label: "HOME" },
  { to: "/shop", label: "SHOP" },
  { to: "/lookbook", label: "LOOKBOOK" },
  { to: "/collections", label: "COLLECTIONS" },
  { to: "/about", label: "ABOUT" },
  { to: "/contact", label: "CONTACT" },
];

export default function Navbar() {
  const { count, setDrawerOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-wire bg-obsidian/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="nav-logo" className="font-script text-lg sm:text-xl text-neutral-100 hover:opacity-80 transition-opacity">
            Dejon Richh
          </Link>

          <nav className="hidden lg:flex items-center gap-10" data-testid="nav-links">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-mono text-[11px] tracking-[0.3em] transition-colors ${
                    isActive ? "text-white border-b border-white pb-1" : "text-neutral-400 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/account" data-testid="nav-account-link"
              className="border border-wire p-2.5 text-neutral-200 transition-colors hover:border-white hover:text-white" aria-label="Account">
              <User size={14} strokeWidth={1.5} />
            </Link>
            <button
              data-testid="nav-cart-button"
              onClick={() => setDrawerOpen(true)}
              className="relative flex items-center gap-2 border border-wire px-4 py-2 font-mono text-[11px] tracking-[0.25em] text-neutral-200 transition-colors hover:border-white hover:text-white"
              aria-label="Open cart"
            >
              <ShoppingBag size={14} strokeWidth={1.5} />
              <span className="hidden sm:inline">CART</span>
              <span data-testid="nav-cart-count" className="text-neutral-500">({count})</span>
            </button>
            <button
              data-testid="nav-menu-button"
              onClick={() => setMenuOpen(true)}
              className="lg:hidden border border-wire p-2 text-neutral-200 hover:border-white"
              aria-label="Open menu"
            >
              <Menu size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-obsidian noise-overlay flex flex-col"
          >
            <div className="flex h-16 items-center justify-between px-5 border-b border-wire">
              <span className="font-script text-lg text-neutral-100">Dejon Richh</span>
              <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} className="border border-wire p-2 text-neutral-200" aria-label="Close menu">
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.08 * i }}>
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    onClick={() => setMenuOpen(false)}
                    className={`block py-3 font-display text-4xl font-extrabold uppercase tracking-tight ${
                      location.pathname === l.to ? "text-white" : "text-neutral-600 hover:text-white"
                    } transition-colors`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="flex items-center justify-between px-8 pb-10">
              <p className="font-mono text-[10px] tracking-[0.3em] text-neutral-600">
                MORE THAN CLOTHING. IT'S AN OUTFIT.
              </p>
              <Link to="/admin" data-testid="mobile-nav-admin" onClick={() => setMenuOpen(false)}
                className="font-mono text-[10px] tracking-[0.3em] text-neutral-500 underline underline-offset-4 hover:text-white">
                ADMIN
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
